/**
 * Patient Service
 * Handles patient-related operations using real API
 */

import {
  patientsApi,
  type ListPatientsParams,
  type FindDuplicatesParams,
  type DuplicatePatientMatch,
  type MergePatientsPayload,
  type PatientAutocompleteItem,
  type PortalInvitationResponse,
} from './api/patientsApi';
import { mapPatientFromApi, mapApiError } from '@/types/api-mappers';
import { calculateAge } from '@/utils/formatters';
import type { Patient, ApiResponse } from './types';

// CORA-153 — adapt the minimal autocomplete projection to the frontend `Patient`
// shape the typeahead surfaces already render. The projection intentionally omits
// most fields; we fill the few the `Patient` type requires with neutral values
// and derive `age` from the civil DOB so result rows show "(N years)" correctly.
// Callers that need the full record fetch it by id on selection.
function mapAutocompleteToPatient(item: PatientAutocompleteItem): Patient {
  return {
    id: item.id,
    patientCode: item.patientCode,
    firstName: item.firstName,
    lastName: item.lastName,
    dateOfBirth: item.dateOfBirth,
    age: calculateAge(item.dateOfBirth),
    gender: 'other',
    phone: item.phone,
    email: '',
    address: '',
    status: 'active',
    createdAt: '',
    updatedAt: '',
  };
}

/** CORA-151 — frontend shape of a successful merge: the post-merge primary
 * patient (mapped to the domain `Patient`), the audit row id, and the per-table
 * re-parent counts. */
export interface PatientMergeOutcome {
  patient: Patient;
  auditId: string;
  reparentedCounts: Record<string, number>;
}

class PatientService {
  /**
   * Get all patients with pagination. `total` is the server's count for the
   * applied filter (used by the patients list to show a result count alongside
   * the loaded page); existing callers that ignore it are unaffected.
   */
  async getAll(params?: ListPatientsParams): Promise<ApiResponse<Patient[]> & { total?: number }> {
    try {
      const response = await patientsApi.list(params);

      return {
        success: true,
        data: response.items.map(mapPatientFromApi),
        total: response.total,
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * Get patient by ID
   */
  async getById(id: string): Promise<ApiResponse<Patient>> {
    try {
      const patient = await patientsApi.getById(id);

      return {
        success: true,
        data: mapPatientFromApi(patient),
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * Get the patient record linked to the authenticated user.
   * Only callable as a PATIENT.
   */
  async getMyProfile(): Promise<ApiResponse<Patient>> {
    try {
      const patient = await patientsApi.getMyProfile();
      return {
        success: true,
        data: mapPatientFromApi(patient),
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * CORA-153 — patient autocomplete (typeahead). Thin wrapper over the shared
   * `GET /patients/search` endpoint returning the minimal, org-scoped projection
   * (patientCode for display, never the UUID). `limit` caps the suggestions.
   */
  async autocomplete(query: string, limit?: number): Promise<ApiResponse<PatientAutocompleteItem[]>> {
    try {
      const items = await patientsApi.autocomplete(query, limit);
      return { success: true, data: items };
    } catch (error: any) {
      return { success: false, error: mapApiError(error) };
    }
  }

  /**
   * Search patients by name, phone, ID, or date of birth. Shares the single
   * server-side autocomplete path (CORA-153) with every other typeahead surface;
   * the minimal projection is adapted to the `Patient` shape the existing result
   * rows render. Callers needing the full record fetch it by id on selection.
   */
  async search(query: string, limit?: number): Promise<ApiResponse<Patient[]>> {
    try {
      const items = await patientsApi.search(query, limit);

      return {
        success: true,
        data: items.map(mapAutocompleteToPatient),
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * Create new patient
   */
  async create(patientData: any): Promise<ApiResponse<Patient>> {
    try {
      const patient = await patientsApi.create(patientData);

      return {
        success: true,
        data: mapPatientFromApi(patient),
        message: 'Patient created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * Update patient
   */
  async update(id: string, patientData: any): Promise<ApiResponse<Patient>> {
    try {
      const patient = await patientsApi.update(id, patientData);

      return {
        success: true,
        data: mapPatientFromApi(patient),
        message: 'Patient updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * Delete patient
   */
  async delete(id: string): Promise<ApiResponse<void>> {
    try {
      await patientsApi.delete(id);

      return {
        success: true,
        message: 'Patient deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: mapApiError(error),
      };
    }
  }

  /**
   * CORA-66: admin override for the no-show auto-block. Surfaces the audited
   * unblock endpoint (`POST /patients/:id/unblock-booking`). Reason is required
   * and persisted to the audit trail server-side.
   */
  async unblockBooking(id: string, reason: string): Promise<ApiResponse<Patient>> {
    try {
      const updated = await patientsApi.unblockBooking(id, reason);
      return { success: true, data: mapPatientFromApi(updated) };
    } catch (error: any) {
      return { success: false, error: mapApiError(error) };
    }
  }

  /**
   * CORA-148/149: fuzzy duplicate detection for the registration/intake flows.
   * Returns ranked candidate matches (highest score first) so staff can be
   * warned about likely existing records before creating a new patient. This is
   * a soft, non-blocking signal — it never prevents the create.
   */
  async findDuplicates(params: FindDuplicatesParams): Promise<ApiResponse<DuplicatePatientMatch[]>> {
    try {
      const { matches } = await patientsApi.findDuplicates(params);
      return { success: true, data: matches };
    } catch (error: any) {
      return { success: false, error: mapApiError(error) };
    }
  }

  /**
   * CORA-154: email the patient a single-use portal activation link. The
   * backend rejects this when the patient has no email, already has portal
   * access, or no specific hospital is active, and rate limits it — all of
   * which arrive as a message the caller should show rather than swallow.
   */
  async sendPortalInvitation(id: string): Promise<ApiResponse<PortalInvitationResponse>> {
    try {
      const result = await patientsApi.sendPortalInvitation(id);
      return { success: true, data: result };
    } catch (error: any) {
      return { success: false, error: mapApiError(error) };
    }
  }

  /**
   * Resend portal access for a patient who already has an account (emails a
   * password-reset link). Rejected server-side when there is no account, the
   * record is erased, or the account is deactivated.
   */
  async resendPortalAccess(id: string): Promise<ApiResponse<PortalInvitationResponse>> {
    try {
      const result = await patientsApi.resendPortalAccess(id);
      return { success: true, data: result };
    } catch (error: any) {
      return { success: false, error: mapApiError(error) };
    }
  }

  /**
   * CORA-150/151: merge a duplicate (secondary) patient into the primary.
   * IRREVERSIBLE — the secondary is soft-deleted. Admin-only server-side.
   */
  async merge(primaryId: string, payload: MergePatientsPayload): Promise<ApiResponse<PatientMergeOutcome>> {
    try {
      const result = await patientsApi.merge(primaryId, payload);
      return {
        success: true,
        data: {
          patient: mapPatientFromApi(result.patient),
          auditId: result.auditId,
          reparentedCounts: result.reparentedCounts,
        },
      };
    } catch (error: unknown) {
      return { success: false, error: mapApiError(error, 'patients.merge.failed') };
    }
  }
}

export const patientService = new PatientService();
